import { useState, useEffect } from 'react';
import { Home, ArrowRight } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';

type FlowVariant = 'standard' | 'partner';

const LOADING_STEPS: Record<FlowVariant, Array<{ label: string; targetPercent: number; durationMs: number }>> = {
  standard: [
    { label: 'Profil erstellen …', targetPercent: 25, durationMs: 800 },
    { label: 'Schwangerschaft anlegen …', targetPercent: 50, durationMs: 1500 },
    { label: 'Inhalte vorbereiten …', targetPercent: 80, durationMs: 1200 },
    { label: 'Dashboard einrichten …', targetPercent: 100, durationMs: 1800 },
  ],
  partner: [
    { label: 'Profil erstellen …', targetPercent: 25, durationMs: 800 },
    { label: 'Verbindung herstellen …', targetPercent: 50, durationMs: 1500 },
    { label: 'Daten synchronisieren …', targetPercent: 80, durationMs: 1200 },
    { label: 'Dashboard einrichten …', targetPercent: 100, durationMs: 1800 },
  ],
};

interface CompletionStepProps {
  onFinish: () => void;
  customMessage?: string;
  /** Which loading animation texts to show. Defaults to 'standard'. */
  variant?: FlowVariant;
}

export function CompletionStep({ onFinish, customMessage, variant = 'standard' }: CompletionStepProps) {
  const { t } = useTranslation('onboarding');
  const [animDone, setAnimDone] = useState(false);

  if (!animDone) {
    return (
      <LoadingAnimation
        steps={LOADING_STEPS[variant]}
        onDone={() => setAnimDone(true)}
      />
    );
  }

  return (
    <div className="p-6 text-center animate-fadeIn">
      {/* ParentPath Logo */}
      <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center">
        <img
          src="https://parentpath.de/pp_logo-piktogram-w100.svg"
          alt="ParentPath"
          className="w-16 h-16"
        />
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mb-2">
        {t('completion.title')}
      </h1>

      {/* Description */}
      <p className="text-white/70 mb-8">
        {customMessage || t('completion.description')}
      </p>

      {/* Features Card */}
      <div className="bg-white/10 rounded-[15px] border border-white/20 p-6 mb-8 text-left">
        <p className="text-sm font-medium text-white/50 mb-4">{t('completion.actionsTitle')}</p>
        <ul className="space-y-3">
          <li className="flex items-start gap-3 text-white/70">
            <span className="w-6 h-6 bg-pp-orange/20 rounded-full flex items-center justify-center text-pp-orange mt-0.5 flex-shrink-0">
              ✓
            </span>
            <span>{t('completion.action1')}</span>
          </li>
          <li className="flex items-start gap-3 text-white/70">
            <span className="w-6 h-6 bg-pp-orange/20 rounded-full flex items-center justify-center text-pp-orange mt-0.5 flex-shrink-0">
              ✓
            </span>
            <span>{t('completion.action2')}</span>
          </li>
          <li className="flex items-start gap-3 text-white/70">
            <span className="w-6 h-6 bg-pp-orange/20 rounded-full flex items-center justify-center text-pp-orange mt-0.5 flex-shrink-0">
              ✓
            </span>
            <span>{t('completion.action3')}</span>
          </li>
          <li className="flex items-start gap-3 text-white/70">
            <span className="w-6 h-6 bg-pp-orange/20 rounded-full flex items-center justify-center text-pp-orange mt-0.5 flex-shrink-0">
              ✓
            </span>
            <span>{t('completion.action4')}</span>
          </li>
        </ul>
      </div>

      {/* CTA */}
      <button
        onClick={onFinish}
        className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors flex items-center justify-center"
      >
        <Home className="w-4 h-4 mr-2" />
        {t('completion.dashboard')}
        <ArrowRight className="w-4 h-4 ml-2" />
      </button>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out both;
        }
      `}</style>
    </div>
  );
}

// ─── Loading Animation ──────────────────────────────────────────────────────

interface LoadingAnimationProps {
  steps: Array<{ label: string; targetPercent: number; durationMs: number }>;
  onDone: () => void;
}

function LoadingAnimation({ steps, onDone }: LoadingAnimationProps) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const ctrl = { cancelled: false };
    let currentProgress = 0;

    async function run() {
      for (let i = 0; i < steps.length; i++) {
        if (ctrl.cancelled) return;
        setActiveIndex(i);

        const step = steps[i];
        const startP = currentProgress;
        const deltaP = step.targetPercent - startP;
        const frames = Math.ceil(step.durationMs / 16);

        for (let f = 0; f <= frames; f++) {
          if (ctrl.cancelled) return;
          const t = f / frames;
          const eased = 1 - Math.pow(1 - t, 2.5);
          currentProgress = startP + deltaP * eased;
          setProgress(currentProgress);
          await new Promise(r => setTimeout(r, 16));
        }

        currentProgress = step.targetPercent;
        setProgress(currentProgress);
      }

      await new Promise(r => setTimeout(r, 500));
      if (!ctrl.cancelled) onDone();
    }

    run();
    return () => { ctrl.cancelled = true; };
  }, [steps, onDone]);

  const currentLabel = activeIndex >= 0 ? steps[activeIndex].label : '';

  return (
    <div className="flex flex-col items-center py-8">
      <span
        key={activeIndex}
        className="text-white text-sm font-medium text-center"
        style={{ animation: 'slideUpFadeIn 0.4s ease-out both' }}
      >
        {currentLabel}
      </span>

      <div className="w-full max-w-[240px] h-2 bg-white/10 rounded-full overflow-hidden" style={{ marginTop: 20 }}>
        <div
          className="h-full rounded-full bg-pp-teal transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>

      <style>{`
        @keyframes slideUpFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
